import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export function useDashboardStats() {
  return useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: async () => {
      // Fetch leads count
      const { count: leadsCount, error: leadsError } = await supabase
        .from("leads")
        .select("*", { count: "exact", head: true });

      if (leadsError) throw leadsError;

      // Fetch deals with amounts
      const { data: deals, error: dealsError } = await supabase
        .from("deals")
        .select("amount, status");

      if (dealsError) throw dealsError;

      // Fetch today's calls
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const { count: callsCount, error: callsError } = await supabase
        .from("calls")
        .select("*", { count: "exact", head: true })
        .gte("created_at", today.toISOString());

      if (callsError) throw callsError;

      const pipelineValue = deals
        .filter((d) => d.status === "open")
        .reduce((sum, d) => sum + (Number(d.amount) || 0), 0);

      const wonDeals = deals.filter((d) => d.status === "won").length;
      const totalDeals = deals.length;
      const winRate = totalDeals > 0 ? Math.round((wonDeals / totalDeals) * 100) : 0;

      return {
        activeLeads: leadsCount || 0,
        pipelineValue,
        winRate,
        callsToday: callsCount || 0,
      };
    },
  });
}

export function useHotLeads() {
  return useQuery({
    queryKey: ["hot-leads"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leads")
        .select("*")
        .order("ai_conversion_score", { ascending: false })
        .limit(5);

      if (error) throw error;
      return data;
    },
  });
}

export function usePipelineOverview() {
  return useQuery({
    queryKey: ["pipeline-overview"],
    queryFn: async () => {
      const { data: stages, error: stagesError } = await supabase
        .from("pipeline_stages")
        .select("*")
        .order("position", { ascending: true });

      if (stagesError) throw stagesError;

      const { data: deals, error: dealsError } = await supabase
        .from("deals")
        .select("stage_id, amount, status")
        .eq("status", "open");

      if (dealsError) throw dealsError;

      return stages.map((stage) => {
        const stageDeals = deals.filter((d) => d.stage_id === stage.id);
        const value = stageDeals.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);

        return {
          id: stage.id,
          name: stage.name,
          color: stage.color || "#06B6D4",
          count: stageDeals.length,
          value,
        };
      });
    },
  });
}

export function useUserProfile() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["user-profile", user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
}

export interface RecentActivity {
  id: string;
  type: "call" | "deal" | "proposal" | "lead";
  title: string;
  description: string;
  time: string;
  created_at: string;
}

export function useRecentActivity() {
  return useQuery({
    queryKey: ["recent-activity"],
    queryFn: async () => {
      const activities: RecentActivity[] = [];

      // Fetch recent calls
      const { data: calls } = await supabase
        .from("calls")
        .select("id, created_at, direction, status, contacts(first_name, last_name), leads(first_name, last_name)")
        .order("created_at", { ascending: false })
        .limit(3);

      calls?.forEach((call) => {
        const contactName = call.contacts 
          ? `${call.contacts.first_name} ${call.contacts.last_name}`
          : call.leads
          ? `${call.leads.first_name} ${call.leads.last_name}`
          : "Unknown";
        activities.push({
          id: `call-${call.id}`,
          type: "call",
          title: `${call.direction === "inbound" ? "Inbound" : "Outbound"} call`,
          description: `${call.status} call with ${contactName}`,
          time: call.created_at,
          created_at: call.created_at,
        });
      });

      // Fetch recent deals
      const { data: deals } = await supabase
        .from("deals")
        .select("id, title, status, amount, created_at, updated_at")
        .order("updated_at", { ascending: false })
        .limit(3);

      deals?.forEach((deal) => {
        const amount = deal.amount ? `$${Number(deal.amount).toLocaleString()}` : "";
        activities.push({
          id: `deal-${deal.id}`,
          type: "deal",
          title: deal.status === "won" ? "Deal won!" : deal.status === "lost" ? "Deal lost" : "Deal updated",
          description: `${deal.title}${amount ? ` - ${amount}` : ""}`,
          time: deal.updated_at,
          created_at: deal.updated_at,
        });
      });

      // Fetch recent proposals
      const { data: proposals } = await supabase
        .from("proposals")
        .select("id, title, status, created_at, sent_at")
        .order("created_at", { ascending: false })
        .limit(3);

      proposals?.forEach((proposal) => {
        activities.push({
          id: `proposal-${proposal.id}`,
          type: "proposal",
          title: proposal.status === "sent" ? "Proposal sent" : "Proposal created",
          description: proposal.title,
          time: proposal.sent_at || proposal.created_at,
          created_at: proposal.sent_at || proposal.created_at,
        });
      });

      // Fetch recent leads
      const { data: leads } = await supabase
        .from("leads")
        .select("id, first_name, last_name, company_name, created_at")
        .order("created_at", { ascending: false })
        .limit(3);

      leads?.forEach((lead) => {
        activities.push({
          id: `lead-${lead.id}`,
          type: "lead",
          title: "New lead added",
          description: `${lead.first_name} ${lead.last_name}${lead.company_name ? ` from ${lead.company_name}` : ""}`,
          time: lead.created_at,
          created_at: lead.created_at,
        });
      });

      // Sort by time and return top 5
      return activities
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 5);
    },
  });
}

export interface AIInsight {
  id: string;
  title: string;
  description: string;
  action: string;
  actionRoute?: string;
}

export function useAIInsights() {
  return useQuery({
    queryKey: ["ai-insights"],
    queryFn: async () => {
      const insights: AIInsight[] = [];
      const today = new Date();
      const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

      // Get deals closing soon with high probability
      const { data: closingDeals } = await supabase
        .from("deals")
        .select("id, title, expected_close_date, ai_win_probability, amount")
        .eq("status", "open")
        .gte("ai_win_probability", 70)
        .lte("expected_close_date", weekFromNow.toISOString())
        .order("ai_win_probability", { ascending: false })
        .limit(5);

      if (closingDeals && closingDeals.length > 0) {
        const totalValue = closingDeals.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
        insights.push({
          id: "closing-deals",
          title: `${closingDeals.length} deal${closingDeals.length > 1 ? "s" : ""} ready to close this week`,
          description: `High probability deals worth $${totalValue.toLocaleString()} are approaching close date. Prioritize follow-ups today.`,
          action: "View deals",
          actionRoute: "/app/deals",
        });
      }

      // Get hot leads with high scores
      const { data: hotLeads } = await supabase
        .from("leads")
        .select("id, ai_conversion_score")
        .gte("ai_conversion_score", 80)
        .in("status", ["new", "engaged", "qualified"]);

      if (hotLeads && hotLeads.length > 0) {
        const avgScore = Math.round(hotLeads.reduce((sum, l) => sum + (l.ai_conversion_score || 0), 0) / hotLeads.length);
        insights.push({
          id: "hot-leads",
          title: `${hotLeads.length} high-potential lead${hotLeads.length > 1 ? "s" : ""} detected`,
          description: `AI scoring identified leads with ${avgScore}% average conversion probability. These leads show strong buying signals.`,
          action: "View leads",
          actionRoute: "/app/leads",
        });
      }

      // Get scheduled calls today
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);

      const { data: scheduledCalls } = await supabase
        .from("calls")
        .select("id")
        .eq("status", "scheduled")
        .gte("scheduled_at", todayStart.toISOString())
        .lte("scheduled_at", todayEnd.toISOString());

      if (scheduledCalls && scheduledCalls.length > 0) {
        insights.push({
          id: "scheduled-calls",
          title: `${scheduledCalls.length} call${scheduledCalls.length > 1 ? "s" : ""} scheduled today`,
          description: `You have upcoming calls to prepare for. Review lead information and talking points before each call.`,
          action: "View calls",
          actionRoute: "/app/calls",
        });
      }

      // Get pending proposals
      const { data: pendingProposals } = await supabase
        .from("proposals")
        .select("id, title")
        .eq("status", "sent")
        .is("responded_at", null);

      if (pendingProposals && pendingProposals.length > 0) {
        insights.push({
          id: "pending-proposals",
          title: `${pendingProposals.length} proposal${pendingProposals.length > 1 ? "s" : ""} awaiting response`,
          description: `Follow up on sent proposals to increase response rates. Consider scheduling calls with prospects who haven't responded.`,
          action: "View proposals",
          actionRoute: "/app/proposals",
        });
      }

      // Default insight if no data
      if (insights.length === 0) {
        insights.push({
          id: "getting-started",
          title: "Start building your pipeline",
          description: "Add leads, create deals, and let AI help you prioritize your sales activities for maximum impact.",
          action: "Add lead",
          actionRoute: "/app/leads",
        });
      }

      return insights.slice(0, 2); // Return top 2 insights
    },
  });
}
